import { BodyText } from "@/components/ui/Text";
import { captureError } from "@/lib/monitoring";
import { formatDate } from "date-fns";
import { memo } from "react";
import type { ChatMessage, ChatUserMap } from "../../types/chatMessageTypes";
import { AsyncMessageTypes, MessageType } from "../../types/chatMessageTypes";
import { AutochatFailureMessage } from "./AutochatFailureMessage";
import { CarePlanMessage } from "./CarePlanMessage";
import { ClosedForInactivityMessage } from "./ClosedForInactivityMessage";
import { ConfirmLabsMessage } from "./ConfirmLabsMessage";
import { EncounterCloseMessage } from "./EncounterCloseMessage";
import { EncounterEndMessage } from "./EncounterEndMessage";
import { EncounterReopenMessage } from "./EncounterReopenMessage";
import { EncounterStartMessage } from "./EncounterStartMessage";
import { EscalateToMdMessage } from "./EscalateToMdMessage";
import { GenericSystemMessage } from "./GenericSystemMessage";
import { HandoffMessage } from "./HandoffMessage";
import { InactivePatientMessage } from "./InactivePatientMessage";
import { MentalHealthQuestionnaireMessage } from "./MentalHealthQuestionnaireMessage";
import { NoteClosedMessage } from "./NoteClosedMessage";
import { NoteStartedMessage } from "./NoteStartedMessage";
import { PhotoMessage } from "./PhotoMessage";
import { PinMessage } from "./PinMessage";
import { ProviderBioMessage } from "./ProviderBioMessage";
import { StealMessage } from "./StealMessage";
import { SurveyMessage } from "./SurveyMessage";
import { TextMessage } from "./TextMessage";
import { VideoCallMessage } from "./VideoCallMessage";
import { WaitTimeMessage } from "./WaitTimeMessage";

/**
 * Props for the MessageFactory component
 * @interface MessageFactoryProps
 * @property {ChatMessage} message - The chat message to be rendered
 * @property {ChatUserMap} [userMap] - A map of user IDs to ChatUser objects, used to look up
 * user information for message attribution and display purposes
 * @property {boolean} isCallPending - Whether a video call is pending
 * @property {function} toggleVideoVisitCall - Function to toggle a video call
 * @property {string} encounterId - The encounter ID
 */
interface MessageFactoryProps {
  message: ChatMessage;
  userMap?: ChatUserMap;
  isCallPending: boolean;
  toggleVideoVisitCall: (encounterId: string) => void;
  encounterId?: string;
  patientDisplayName?: string;
}

export const MessageFactory = memo(
  ({
    message,
    userMap,
    isCallPending,
    toggleVideoVisitCall,
    encounterId,
    patientDisplayName,
  }: MessageFactoryProps) => {
    // Render the appropriate component based on message type
    switch (message.type) {
      case MessageType.TEXT:
      case AsyncMessageTypes.TEXT:
        return <TextMessage message={message} />;
      case MessageType.PHOTO:
      case AsyncMessageTypes.PHOTO:
        return (
          <PhotoMessage
            message={message}
            userMap={userMap}
            patientDisplayName={patientDisplayName}
          />
        );
      case MessageType.VIDEO_CALL:
        return (
          <VideoCallMessage
            message={message}
            userMap={userMap}
            isCallPending={isCallPending}
            toggleVideoVisitCall={() => toggleVideoVisitCall(encounterId || "")}
            encounterId={encounterId}
          />
        );
      case MessageType.QUESTIONNAIRE:
        return <MentalHealthQuestionnaireMessage message={message} />;
      case MessageType.AUTOCHAT_FAILURE:
        return <AutochatFailureMessage message={message} />;
      case MessageType.CARE_PLAN:
      case AsyncMessageTypes.CARE_PLAN:
        return <CarePlanMessage message={message} userMap={userMap} />;
      case MessageType.ENCOUNTER_START:
        return <EncounterStartMessage message={message} />;
      case MessageType.WAIT_TIME:
        return <WaitTimeMessage />;
      case MessageType.HANDOFF:
        return <HandoffMessage message={message} />;
      case MessageType.NOTE_STARTED:
        return <NoteStartedMessage message={message} />;
      case MessageType.NOTE_CLOSED:
        return <NoteClosedMessage message={message} />;
      case MessageType.STEAL:
        return <StealMessage message={message} />;
      case MessageType.ESCALATE_TO_MD:
        return <EscalateToMdMessage />;
      case MessageType.PROVIDER_BIO:
        return <ProviderBioMessage message={message} />;
      case MessageType.ENCOUNTER_REOPEN:
      case AsyncMessageTypes.REOPEN:
        return <EncounterReopenMessage />;
      case MessageType.ENCOUNTER_END:
        return <EncounterEndMessage />;
      case MessageType.ENCOUNTER_CLOSE:
        return <EncounterCloseMessage message={message} />;
      case MessageType.INCOMPLETE_ON_CLOSE:
        return null; // We don't render this message type anymore, but it still gets sent from the server
      case MessageType.SURVEY_MESSAGE:
        return <SurveyMessage />;
      case MessageType.INACTIVE_PATIENT:
        return <InactivePatientMessage message={message} />;
      case MessageType.CLOSED_FOR_INACTIVITY:
        return <ClosedForInactivityMessage />;
      case MessageType.CONFIRM_LABS:
        return <ConfirmLabsMessage message={message} />;
      case MessageType.CONFIRM_LABS_RESPONSE:
        return null; // This message is only used as notification, not persisted or rendered
      // Deprecated message types that we render for continuity in older chats
      case MessageType.PIN:
        return <PinMessage message={message} />;
      case MessageType.CONFIRM_RX:
        return (
          <GenericSystemMessage text="Prescription sent to patient for confirmation." />
        );
      case MessageType.AR_QUESTION:
        return (
          <GenericSystemMessage text="Assisted response question sent to patient." />
        );
      case MessageType.VERIFICATION_REQUEST:
        return (
          <GenericSystemMessage text="Patient ID verification requested." />
        );
      case MessageType.VERIFICATION_CONFIRMATION:
        return (
          <GenericSystemMessage text="Patient ID verification confirmed." />
        );
      case MessageType.PMH_QUESTION:
        return (
          <GenericSystemMessage text="Past medical history question sent to patient." />
        );
      case MessageType.PATIENT_INFO:
        return (
          <GenericSystemMessage text="Information request sent to patient." />
        );
      case MessageType.AUTOSEND_INTRO:
        return <GenericSystemMessage text="Autochat intro sent to patient." />;
      case MessageType.ARTICLE:
        return <GenericSystemMessage text="Article sent to patient." />;
      case MessageType.SINGLE_SELECT:
        return (
          <GenericSystemMessage text="Single-select question sent to patient." />
        );
      case MessageType.MULTI_SELECT:
        return (
          <GenericSystemMessage text="Multi-select question sent to patient." />
        );
      case MessageType.CHIEF_COMPLAINT:
        return (
          <GenericSystemMessage text="Chief complaint message sent to patient." />
        );
      case MessageType.CONFIRM_CHIEF_COMPLAINT:
        return (
          <GenericSystemMessage text="Confirm chief complaint message sent to patient." />
        );
      case MessageType.NEXT_QUESTION:
        return (
          <GenericSystemMessage text="Next question message sent to patient." />
        );
      case MessageType.INITIATE_RATING:
        return <GenericSystemMessage text="Ratings message sent to patient." />;
      case MessageType.SYMPTOM_EXTRACTOR:
        return (
          <GenericSystemMessage text="Symptom extractor message sent to patient." />
        );
      case MessageType.INTENT:
        return <GenericSystemMessage text="Intent message sent to patient." />;
      case MessageType.CONTENT_SHARE:
        return (
          <GenericSystemMessage text="Content sharing message sent to patient." />
        );
      case MessageType.WAIT_INTAKE_REVIEW:
        return (
          <GenericSystemMessage text="Intake review message sent to patient." />
        );
      case MessageType.CONTENT_WHILE_YOU_WAIT:
        return (
          <GenericSystemMessage text="Content while you wait card sent to patient." />
        );
      default:
        captureError(
          new Error(`Unknown message type in MessageFactory: ${message.type}`),
        );
        // Placeholder as we build out the rest of the message types
        return (
          <div className="flex flex-col gap-2 justify-center items-center">
            <BodyText size="xs" textColor="primary">
              Unsupported message type:{" "}
              {MessageType[message.type] || message.type}
            </BodyText>
            <BodyText size="xs" textColor="tertiary">
              {formatDate(message.created, "h:mm a")}
            </BodyText>
          </div>
        );
    }
  },
);

MessageFactory.displayName = "MessageFactory";
